import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { useTranslation } from 'react-i18next';
import type { TradeFlowPipelineTotalProps } from './tradeFlowPipeline.types';
import { stageDotColor } from './tradeFlowPipeline.utils';

export const TradeFlowPipelineTotal = ({
  stages,
  totalLabelOverride,
}: TradeFlowPipelineTotalProps) => {
  const { sys } = useThemeTokens();
  const { t } = useTranslation(['portfolioTradingHub']);
  const total = stages.reduce((acc, stage) => acc + stage.count, 0);

  return (
    <Box>
      <Typography
        variant='labelSmall'
        sx={{
          mt: 1,
          mb: 0.5,
          textTransform: 'uppercase',
          color: sys.color.onSurfaceVariant,
        }}
      >
        {totalLabelOverride ??
          t('tradePipeline.totalOutstanding', {
            total: total.toLocaleString(),
          })}
      </Typography>

      <Box
        data-testid='trade-flow-pipeline-stacked-bar'
        sx={{
          display: 'flex',
          width: '100%',
          height: 20,
          borderRadius: 1,
          overflow: 'hidden',
          backgroundColor: sys.color.surfaceContainer,
        }}
      >
        {total === 0 ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant='labelSmall'
              sx={{ color: sys.color.onSurfaceVariant }}
            >
              {t('tradePipeline.emptyBar')}
            </Typography>
          </Box>
        ) : (
          stages
            .filter((stage) => stage.count > 0)
            .map((stage) => {
              const pct = (stage.count / total) * 100;
              return (
                <Box
                  key={stage.id}
                  data-testid={`trade-flow-pipeline-segment-${stage.id}`}
                  sx={{
                    flexBasis: `${pct}%`,
                    backgroundColor: stageDotColor(sys.color, stage.colorKey),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {pct >= 6 && (
                    <Typography
                      variant='labelSmall'
                      sx={{ color: sys.color.onPrimary ?? '#fff' }}
                    >
                      {stage.count}
                    </Typography>
                  )}
                </Box>
              );
            })
        )}
      </Box>

      <Box
        data-testid='trade-flow-pipeline-legend'
        display='flex'
        flexWrap='wrap'
        gap={1.5}
        mt={0.75}
      >
        {stages.map((stage) => (
          <Box key={stage.id} display='flex' alignItems='center' gap={0.75}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: stageDotColor(sys.color, stage.colorKey),
              }}
            />
            <Typography
              variant='bodySmall'
              sx={{ color: sys.color.onSurfaceVariant }}
            >
              {stage.title.charAt(0).toUpperCase() +
                stage.title.slice(1).toLowerCase()}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default TradeFlowPipelineTotal;
